/*
 * Copyright (c) 2019 AccelByte Inc. All Rights Reserved
 * This is licensed software from AccelByte Inc, for limitations
 * and restrictions contact your company contract manager.
 */

import * as React from "react";
import classnames from "classnames";
import { Trans } from "react-i18next";
import { FlexGrid, Select, WithPropsGuard } from "../../../_common";
import styles from "./DocumentFilter.module.scss";
import { LocalizedPolicyInformation, LocalizedPolicy, PolicyList } from "../../../../../api/legal/models/policies";
import { Subscribe } from "unstated";
import { AppState } from "../../../../../app-state/AppState";
import BasicLogic from "../../../../../Logic/Basic/basicLogic";
import { getLanguagesListFromPoliciesList, getLocalizedPolicyInfo } from "../../utils";
import { OptionType } from "../../../_common/Select/Select";
import { t } from "../../../../../i18n/i18n";
import DocumentFilterAccordion from "./DocumentFilterAccordion";
import { Country } from "../../../../../api/basic/models/basic";

interface BaseProps {
  policies: PolicyList;
  localizedPolicy: LocalizedPolicy | LocalizedPolicyInformation | null;
  isLoading: boolean;
  onCountryChange: (countryCode: string) => void;
  onClickPolicy: (localizedPolicy: LocalizedPolicy | LocalizedPolicyInformation) => void;
  activeCountry: string | null;
  className?: string;
  onCloseFilter?: () => void;
}

interface Props extends BaseProps {
  basicLogic: BasicLogic;
}

interface State {
  activeLanguage?: string | null;
}

const DocumentFilter = WithPropsGuard(
  class Index extends React.Component<Props, State> {
    constructor(props: Props) {
      super(props);
      const activeLanguage = this.getActiveCountryAndLanguage().language;
      this.state = { activeLanguage };
    }

    onCountryChange = (selected: OptionType) => {
      this.setState({ activeLanguage: null }, () => {
        this.props.onCountryChange(selected.value);
      });
    };

    renderCountrySelector() {
      const {
        basicLogic: {
          state: { countries, isFetchingCountries, countriesError },
        },
        activeCountry,
      } = this.props;
      const localizedDataCountry = this.getActiveCountryAndLanguage().country;
      const formattedCountries: OptionType[] = [];
      if (!isFetchingCountries && !countriesError) {
        if (!countries) return;
        countries.forEach((country: Country) => {
          formattedCountries.push({ label: country.name, value: country.code });
        });
      }
      return (
        <div>
          <Select
            key={localizedDataCountry}
            options={formattedCountries}
            onChange={this.onCountryChange}
            defaultValue={formattedCountries.filter(
              (country) => country.value.toLowerCase() === (activeCountry || localizedDataCountry || "").toLowerCase()
            )}
            isLoading={isFetchingCountries || !localizedDataCountry}
            isError={!!countriesError}
            placeholder={t("DocumentFilter.countries.placeholder")}
            errorMessage={t("DocumentFilter.countries.error")}
            className={styles.filterSelect}
          />
        </div>
      );
    }

    getActiveCountryAndLanguage(): { language?: string; country?: string } {
      const {
        basicLogic: {
          state: { isFetchingLanguages, languagesError },
        },
        localizedPolicy,
        policies,
      } = this.props;
      if (!localizedPolicy) return {};

      let language: string | undefined = undefined;
      let country: string | undefined = undefined;

      if (!!localizedPolicy) {
        const localizedPolicyInfo = getLocalizedPolicyInfo({ localizedPolicy, policies });
        if (localizedPolicyInfo.countryCode) country = localizedPolicyInfo.countryCode;
      }

      if (!isFetchingLanguages && !languagesError) {
        const policyInfo = getLocalizedPolicyInfo({ localizedPolicy, policies });
        if (policyInfo.localeId) language = policyInfo.localeId;
      }
      return { language, country };
    }

    renderLanguageSelector() {
      const {
        basicLogic: {
          state: { languages, isFetchingLanguages, languagesError },
        },
        policies,
      } = this.props;
      const activeLanguage = this.getActiveCountryAndLanguage().language;
      let formattedLanguage: OptionType[] = [];
      const availableLanguageList = getLanguagesListFromPoliciesList(policies);
      if (!isFetchingLanguages && !languagesError) {
        if (!languages) return;
        formattedLanguage = Object.entries(languages)
          .map(
            (language) =>
              ({
                value: language[0],
                label: language[1],
              } as OptionType)
          )
          .filter((language) => availableLanguageList.includes(language.value));
      }

      return (
        <Select
          key={activeLanguage}
          options={formattedLanguage}
          onChange={(option: OptionType) => {
            this.setState({ activeLanguage: option.value });
          }}
          disabled={formattedLanguage.length === 1}
          defaultValue={formattedLanguage.filter(
            (language) => language.value.toLowerCase() === (activeLanguage || "").toLowerCase()
          )}
          isLoading={isFetchingLanguages || !activeLanguage}
          isError={!!languagesError}
          placeholder={t("DocumentFilter.language.placeholder")}
          errorMessage={t("DocumentFilter.language.error")}
          className={styles.filterSelect}
        />
      );
    }

    renderNamespacePolicyFilter() {
      const { policies, localizedPolicy, onClickPolicy } = this.props;
      const { language } = this.getActiveCountryAndLanguage();
      const { activeLanguage } = this.state;
      if (!language || !localizedPolicy) return null;
      return (
        <DocumentFilterAccordion
          key={`${localizedPolicy.id}${language}`}
          policies={policies}
          onClickPolicy={onClickPolicy}
          activeLanguage={activeLanguage || language}
          activeLocalizedPolicyId={localizedPolicy.id}
        />
      );
    }

    render() {
      const { className, onCloseFilter } = this.props;
      return (
        <FlexGrid className={classnames(styles.documentFilter, className)}>
          <div className={styles.documentFilterMenu}>
            <div className={styles.filterMenu}><Trans i18nKey={"DocumentFilter.menu"}>MENU</Trans></div>
            <div className={styles.filterClose} onClick={(()=> onCloseFilter && onCloseFilter())}/>
          </div>
          <div className={styles.containerSticky}>
            <div className={styles.selection}>
              <div className={styles.title}>
                <Trans i18nKey={"DocumentFilter.countryAndLanguages"}>Country & Language</Trans>
              </div>
              {this.renderCountrySelector()}
              {this.renderLanguageSelector()}
            </div>
            <div className={styles.accordion}>{this.renderNamespacePolicyFilter()}</div>
          </div>
        </FlexGrid>
      );
    }
  },
  (prevProps, nextProps) =>
    (prevProps.localizedPolicy || {}).toString() !== (nextProps.localizedPolicy || {}).toString() ||
    prevProps.policies.toString() !== nextProps.policies.toString()
);

export default (props: BaseProps) => (
  <Subscribe to={[AppState]}>
    {(appState: AppState) => {
      const { basicLogic } = appState.state;
      return <DocumentFilter basicLogic={basicLogic} {...props} />;
    }}
  </Subscribe>
);
